// ═══════════════════════════════════════════════════════════════════════════
// ADMIN > PERMISSOES — UI de gestao de perfis e visualizacao da matriz
// ═══════════════════════════════════════════════════════════════════════════
// Onda E1 (Usuarios x Perfis - write): lista Cognito + chips dos perfis
// atuais, botao Editar abre modal com checkboxes pra atribuir/remover.
// Onda E2 (Matriz - read-only): perfis x permissoes agrupadas por modulo.
//
// Acesso gated por admin.permissoes.editar.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { App as AntApp } from 'antd';
import { usePerms } from '../hooks/usePerms';
import { API_URL } from '../config';
import { getToken } from './operacional-contratos/utils';
import ModalNovaPessoa from '../components/pessoas/ModalNovaPessoa';
import ModalDesligarPessoa, { resumoDesligamento } from '../components/pessoas/ModalDesligarPessoa';
import ModalReativarPessoa from '../components/pessoas/ModalReativarPessoa';
import PainelConferencia from '../components/pessoas/PainelConferencia';

type Perfil = { codigo: string; nome: string; descricao?: string; ativo?: boolean;
                qtd_permissoes?: number; qtd_usuarios?: number;
                possui_permissao_nexo?: boolean };
type Usuario = {
  username: string | null;
  usuario_erp_id: string | null;
  email: string;
  nome: string;
  enabled: boolean;
  status: string;
  criado_em: string | null;
  perfis: Array<{ codigo: string; nome: string }>;
  // Ciclo da pessoa (28/09/2026): ficha ligada ao login, se houver.
  profissional_id?: string | null;
  ficha_nome?: string | null;
  ficha_vigente?: boolean | null;
  desligamento?: string | null;
  conta_sem_vinculo?: boolean;
};
type PermissaoCat = {
  codigo: string; modulo: string; pagina: string; sub_pagina: string | null;
  tipo: string; descricao: string; notas: string | null;
};
type Modulo = { modulo: string; permissoes: PermissaoCat[]; total: number };
type Atribuicao = { perfil_codigo: string; permissao_codigo: string;
                    condicional: boolean; observacao: string | null };
type Matriz = {
  perfis: Perfil[];
  modulos: Modulo[];
  atribuicoes: Atribuicao[];
  total_perfis: number;
  total_permissoes: number;
};

export default function AdminPermissoes() {
  return (
    <AntApp>
      <Inner />
    </AntApp>
  );
}

function Inner() {
  const { pode, carregando: carregandoPerms } = usePerms();
  const [aba, setAba] = useState<'usuarios' | 'matriz' | 'conferencia'>('usuarios');

  if (carregandoPerms) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando…</div>;
  }
  if (!pode('admin.permissoes.editar')) {
    return (
      <div style={{ padding: 40, maxWidth: 560, margin: '40px auto', textAlign: 'center',
        background: '#fff', border: '0.5px solid #FBD9D9', borderRadius: 8 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
        <p style={{ fontSize: 14, color: '#222' }}>
          Sem permissão pra acessar a gestão de perfis (admin.permissoes.editar).
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 28px', maxWidth: 1320, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#020D7F' }}>🔒 Permissões</h2>
        <span style={{ fontSize: 12, color: '#888' }}>
          Gerencia perfis dos usuários e visualiza a matriz de permissões
        </span>
      </div>

      <div style={{ display: 'flex', gap: 18, marginBottom: 14, borderBottom: '0.5px solid #e0e0e0' }}>
        {[
          { id: 'usuarios', label: '👥 Usuários × Perfis' },
          { id: 'matriz',   label: '🧩 Matriz Perfis × Permissões' },
          { id: 'conferencia', label: '🧭 Conferência de pessoas' },
        ].map(t => (
          <button key={t.id} onClick={() => setAba(t.id as any)}
            style={{ fontSize: 13, background: 'none', border: 'none', cursor: 'pointer',
              color: aba === t.id ? '#0930D0' : '#6A7092',
              fontWeight: aba === t.id ? 600 : 400,
              padding: '8px 0',
              borderBottom: `2px solid ${aba === t.id ? '#0930D0' : 'transparent'}` }}>
            {t.label}
          </button>
        ))}
      </div>

      {aba === 'usuarios' && <AbaUsuariosPerfis />}
      {aba === 'matriz'   && <AbaMatriz />}
      {aba === 'conferencia' && (
        <PainelConferencia onAbrirFicha={(id) => { window.location.href = `/administrativo/rh?pessoa=${id}`; }} />
      )}
    </div>
  );
}

// ─── ABA 1: USUARIOS × PERFIS (write) ─────────────────────────────────────
function AbaUsuariosPerfis() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [acaoMenu, setAcaoMenu] = useState<{ email: string; nome: string } | null>(null);
  const [convidandoOrfao, setConvidandoOrfao] = useState<Usuario | null>(null);
  const [editandoDadosProf, setEditandoDadosProf] = useState<{ email: string; nome: string } | null>(null);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_perms_listar_usuarios' },
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_perms_listar_perfis' },
          { headers: { Authorization: token } }),
      ]);
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setUsuarios(d1.usuarios || []);
      setPerfis(d2.perfis || []);
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  const filtrados = useMemo(() => {
    if (!busca.trim()) return usuarios;
    const q = busca.toLowerCase();
    return usuarios.filter(u =>
      u.email.toLowerCase().includes(q) ||
      (u.nome || '').toLowerCase().includes(q) ||
      u.perfis.some(p => p.nome.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q))
    );
  }, [usuarios, busca]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando usuários…</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="🔎 Buscar email, nome ou perfil…"
          value={busca} onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, minWidth: 240, maxWidth: 420, padding: '7px 10px',
            fontSize: 12, borderRadius: 5, border: '0.5px solid #ddd' }} />
        <span style={{ fontSize: 11, color: '#888' }}>
          {filtrados.length} de {usuarios.length} usuário(s)
        </span>
        <button onClick={() => setCriandoNovo(true)}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
            background: '#0930D0', color: '#fff', border: 'none',
            borderRadius: 4, cursor: 'pointer' }}>
          + Novo usuário
        </button>
      </div>

      <div style={{ fontSize: 11, color: '#344054', marginBottom: 14, padding: '9px 11px',
        background: '#F4F7FF', border: '0.5px solid #D6DDFF', borderRadius: 5 }}>
        <strong>Perfis NEXO são adicionais.</strong> Mantenha o perfil do cargo atual da pessoa e marque,
        junto dele, apenas a função que ela exercerá no NEXO.
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff',
        border: '0.5px solid #e0e0e0', borderRadius: 6 }}>
        <thead>
          <tr style={{ background: '#F0F1F5', color: '#555' }}>
            <th style={{ padding: 10, textAlign: 'left', fontSize: 11 }}>Email</th>
            <th style={{ padding: 10, textAlign: 'left', fontSize: 11 }}>Nome</th>
            <th style={{ padding: 10, textAlign: 'left', fontSize: 11 }}>Perfis atuais</th>
            <th style={{ padding: 10, textAlign: 'center', fontSize: 11, width: 90 }}>Status</th>
            <th style={{ padding: 10, textAlign: 'right', fontSize: 11, width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {filtrados.map(u => (
            <tr key={u.email} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 11, color: '#020D7F' }}>
                {u.email}
              </td>
              <td style={{ padding: 10 }}>{u.nome || '—'}</td>
              <td style={{ padding: 10 }}>
                {u.perfis.length === 0 ? (
                  <span style={{ fontSize: 10, color: '#bbb', fontStyle: 'italic' }}>nenhum</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {u.perfis.map(p => (
                      <span key={p.codigo} title={p.codigo}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
                          background: '#EEF1FF', color: '#020D7F' }}>
                        {p.nome}
                      </span>
                    ))}
                  </div>
                )}
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>
                {u.status === 'ORFAO' ? (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600,
                    background: '#FFE9C2', color: '#B07500' }}>órfão</span>
                ) : u.enabled ? (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600,
                    background: '#DCFCE7', color: '#166534' }}>ativo</span>
                ) : (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600,
                    background: '#FEE2E2', color: '#991B1B' }}>desativado</span>
                )}
              </td>
              <td style={{ padding: 10, textAlign: 'right' }}>
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button onClick={() => setEditando(u)}
                    style={{ padding: '5px 10px', fontSize: 11, fontWeight: 500,
                      background: '#fff', color: '#0930D0', border: '0.5px solid #0930D0',
                      borderRadius: 4, cursor: 'pointer' }}>
                    Perfis
                  </button>
                  {u.status === 'ORFAO' ? (
                    // Órfão: oferecer "Convidar" (cria no Cognito preservando perfis)
                    <button onClick={() => setConvidandoOrfao(u)}
                      title="Criar no Cognito (perfis já atribuídos serão preservados)"
                      style={{ padding: '5px 10px', fontSize: 11, fontWeight: 600,
                        background: '#fff', color: '#B07500', border: '0.5px solid #B07500',
                        borderRadius: 4, cursor: 'pointer' }}>
                      ✉️ Convidar
                    </button>
                  ) : (
                    <button onClick={() => setAcaoMenu({ email: u.email, nome: u.nome })}
                      title="Ações"
                      style={{ padding: '5px 8px', fontSize: 12, fontWeight: 600,
                        background: '#fff', color: '#666', border: '0.5px solid #ddd',
                        borderRadius: 4, cursor: 'pointer' }}>
                      ⋮
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editando && (
        <ModalEditarPerfis usuario={editando} perfis={perfis}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); carregar(); }} />
      )}
      {criandoNovo && (
        <ModalNovaPessoa aberto
          onClose={() => setCriandoNovo(false)}
          onSalvo={() => carregar()} />
      )}
      {acaoMenu && (
        <ModalAcoesUsuario email={acaoMenu.email} nome={acaoMenu.nome}
          usuario={usuarios.find(u => u.email === acaoMenu.email)}
          onClose={() => setAcaoMenu(null)}
          onChanged={() => { setAcaoMenu(null); carregar(); }}
          onEditarDadosProfissionais={(email, nome) => {
            setAcaoMenu(null);
            setEditandoDadosProf({ email, nome });
          }} />
      )}
      {convidandoOrfao && (
        // Órfão (perfis no banco, sem conta): vira pessoa pelo ciclo — login
        // + ficha juntos; os perfis antigos entram no formulário.
        <ModalNovaPessoa aberto emailInicial={convidandoOrfao.email} nomeInicial=""
          onClose={() => setConvidandoOrfao(null)}
          onSalvo={() => carregar()} />
      )}
      {editandoDadosProf && (
        <ModalDadosProfissionais email={editandoDadosProf.email} nome={editandoDadosProf.nome}
          onClose={() => setEditandoDadosProf(null)}
          onSaved={() => { setEditandoDadosProf(null); carregar(); }} />
      )}
    </div>
  );
}

function ModalEditarPerfis({ usuario, perfis, onClose, onSaved }: {
  usuario: Usuario; perfis: Perfil[];
  onClose: () => void; onSaved: () => void;
}) {
  const [selecionados, setSelecionados] = useState<Set<string>>(
    new Set(usuario.perfis.map(p => p.codigo))
  );
  const [salvando, setSalvando] = useState(false);

  function toggle(codigo: string) {
    setSelecionados(prev => {
      const n = new Set(prev);
      if (n.has(codigo)) n.delete(codigo); else n.add(codigo);
      return n;
    });
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      const resp = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_atualizar_usuario',
          email: usuario.email,
          usuario_erp_id: usuario.usuario_erp_id,
          perfis_codigos: Array.from(selecionados) },
        { headers: { Authorization: token } });
      const data = resp.data.body ? JSON.parse(resp.data.body) : resp.data;
      const ativ = data.ativados?.length || 0;
      const desat = data.desativados?.length || 0;
      if (ativ + desat === 0) {
        alert('Nenhuma mudança aplicada.');
      } else {
        alert(`Salvo.\n• ${ativ} perfil(is) adicionado(s)\n• ${desat} perfil(is) removido(s)`);
      }
      onSaved();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const originalSet = new Set(usuario.perfis.map(p => p.codigo));
  const houveMudanca = selecionados.size !== originalSet.size
    || Array.from(selecionados).some(c => !originalSet.has(c));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !salvando && onClose()}>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8,
        width: 520, maxWidth: '92%', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#020D7F' }}>
          Perfis de {usuario.nome || usuario.email}
        </h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14, fontFamily: 'monospace' }}>
          {usuario.email}
        </div>

        <div style={{ fontSize: 10, color: '#344054', marginBottom: 12, padding: 8,
          background: '#F4F7FF', borderRadius: 4 }}>
          Perfis NEXO complementam o cargo atual. Não desmarque o perfil do cargo ao liberar o NEXO.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
          {perfis.map(p => {
            const marcado = selecionados.has(p.codigo);
            const nexo = !!p.possui_permissao_nexo;
            const orfaoSemVinculo = usuario.status === 'ORFAO'
              && nexo && !originalSet.has(p.codigo);
            return (
              <label key={p.codigo}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
                  borderRadius: 5, border: `0.5px solid ${marcado ? '#0930D0' : '#e0e0e0'}`,
                  background: marcado ? '#EEF1FF' : '#fff',
                  cursor: orfaoSemVinculo ? 'not-allowed' : 'pointer',
                  opacity: orfaoSemVinculo ? 0.55 : 1 }}>
                <input type="checkbox" checked={marcado}
                  disabled={orfaoSemVinculo}
                  onChange={() => toggle(p.codigo)}
                  style={{ marginTop: 2, cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#020D7F' }}>{p.nome}</span>
                    <span style={{ fontSize: 10, color: '#888', fontFamily: 'monospace' }}>{p.codigo}</span>
                    {nexo && (
                      <span style={{ fontSize: 9, color: '#7A2E0E', background: '#FFF1E8',
                        padding: '1px 5px', borderRadius: 8 }}>NEXO</span>
                    )}
                    {typeof p.qtd_permissoes === 'number' && (
                      <span style={{ fontSize: 10, color: '#888' }}>· {p.qtd_permissoes} perm(s)</span>
                    )}
                  </div>
                  {p.descricao && (
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{p.descricao}</div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: '#fff', color: '#555', border: '0.5px solid #ddd',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando || !houveMudanca}
            style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600,
              background: !houveMudanca ? '#aaa' : (salvando ? '#888' : '#0930D0'),
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: salvando || !houveMudanca ? 'not-allowed' : 'pointer' }}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// (O modal "Criar usuário" saiu em 28/09/2026: pessoa nasce por ModalNovaPessoa —
// login + ficha + organograma juntos. Conta sem vínculo é uma opção dele.)

// ─── MODAL: Ações do usuário (resetar/desativar/excluir/dados prof) ─────
function ModalAcoesUsuario({ email, nome, usuario, onClose, onChanged, onEditarDadosProfissionais }: {
  email: string; nome: string;
  usuario?: Usuario;
  onClose: () => void; onChanged: () => void;
  onEditarDadosProfissionais: (email: string, nome: string) => void;
}) {
  const [salvando, setSalvando] = useState(false);
  // Quem tem ficha se desliga/reativa pelo ciclo da pessoa (pede a data de
  // saída, mexe em perfis, férias e cobrança); o desativar antigo fica só
  // para conta sem vínculo.
  const temFicha = !!usuario?.profissional_id;
  const [desligando, setDesligando] = useState(false);
  const [reativando, setReativando] = useState(false);

  async function executar(action: string, extra?: any, confirmMsg?: string) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action, email, ...(extra || {}) },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.senha_temp) {
        alert(`Senha temporária gerada:\n\n${d.senha_temp}\n\nEnvie pessoalmente. O usuário será forçado a trocar no próximo login.`);
      } else {
        alert('Concluído.');
      }
      onChanged();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir() {
    const conf = window.prompt(`Para confirmar a exclusão, digite o email: ${email}`);
    if (conf !== email) {
      if (conf !== null) alert('Email não confere — exclusão cancelada.');
      return;
    }
    await executar('admin_perms_excluir_usuario', { confirmacao_email: email },
      `Excluir ${email}?\n\nIsso remove o usuário do Cognito + apaga vínculos de perfis. Irreversível.`);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !salvando && onClose()}>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8,
        width: 380, maxWidth: '92%', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#020D7F' }}>
          Ações de {nome || email}
        </h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 18, fontFamily: 'monospace' }}>
          {email}
        </div>

        {temFicha && usuario && (
          <div style={{ fontSize: 11, color: '#344054', marginBottom: 12, padding: '7px 10px',
            background: '#F4F7FF', border: '0.5px solid #D6DDFF', borderRadius: 5 }}>
            Ficha: <strong>{usuario.ficha_nome}</strong>
            {usuario.ficha_vigente === false && <span style={{ color: '#B07500' }}> · desligado{usuario.desligamento ? ` em ${usuario.desligamento.slice(8, 10)}/${usuario.desligamento.slice(5, 7)}/${usuario.desligamento.slice(0, 4)}` : ''}</span>}
          </div>
        )}
        {desligando && usuario?.profissional_id && (
          <ModalDesligarPessoa aberto pessoa={{ id: usuario.profissional_id, nome: usuario.ficha_nome || nome || email, usuario_email: email, desligamento: usuario.desligamento }}
            onClose={() => setDesligando(false)}
            onSalvo={(r) => { setDesligando(false); alert(resumoDesligamento(r)); onChanged(); }} />
        )}
        {reativando && usuario?.profissional_id && (
          <ModalReativarPessoa aberto pessoa={{ id: usuario.profissional_id, nome: usuario.ficha_nome || nome || email, usuario_email: email, desligamento: usuario.desligamento }}
            onClose={() => setReativando(false)}
            onSalvo={() => { setReativando(false); alert('Pessoa reativada: ficha, perfis e login de volta.'); onChanged(); }} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <button onClick={() => onEditarDadosProfissionais(email, nome)}
            disabled={salvando}
            style={{ padding: '10px 14px', fontSize: 12, textAlign: 'left',
              background: '#fff', color: '#020D7F', border: '0.5px solid #ddd',
              borderRadius: 5, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            👤 Dados profissionais (CREA, foto, telefone, LinkedIn)
          </button>
          <button onClick={() => executar('admin_perms_resetar_senha')}
            disabled={salvando}
            style={{ padding: '10px 14px', fontSize: 12, textAlign: 'left',
              background: '#fff', color: '#020D7F', border: '0.5px solid #ddd',
              borderRadius: 5, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            🔑 Resetar senha (gera temporária)
          </button>
          <button onClick={() => temFicha ? setDesligando(true) : executar('admin_perms_desativar_usuario', null,
            `Desativar ${email}? O usuário não conseguirá logar até reativar.`)}
            disabled={salvando}
            style={{ padding: '10px 14px', fontSize: 12, textAlign: 'left',
              background: '#fff', color: '#B07500', border: '0.5px solid #FFE9C2',
              borderRadius: 5, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            {temFicha ? '⏸ Desligar (informa a data de saída)' : '⏸ Desativar (conta sem vínculo)'}
          </button>
          <button onClick={() => temFicha ? setReativando(true) : executar('admin_perms_ativar_usuario')}
            disabled={salvando}
            style={{ padding: '10px 14px', fontSize: 12, textAlign: 'left',
              background: '#fff', color: '#166534', border: '0.5px solid #DCFCE7',
              borderRadius: 5, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            ▶ Reativar
          </button>
          <button onClick={excluir} disabled={salvando}
            style={{ padding: '10px 14px', fontSize: 12, textAlign: 'left',
              background: '#fff', color: '#B91626', border: '0.5px solid #FBD9D9',
              borderRadius: 5, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            🗑️ Excluir usuário (irreversível)
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: '#fff', color: '#555', border: '0.5px solid #ddd',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: Dados profissionais (CREA, foto, telefone, LinkedIn) ────────
function ModalDadosProfissionais({ email, nome, onClose, onSaved }: {
  email: string; nome: string; onClose: () => void; onSaved: () => void;
}) {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    cargo: '', foto_url: '', telefone_comercial: '',
    crea: '', crea_rnp: '', crea_titulo: '', linkedin: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'admin_perms_obter_dados_profissionais', email },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (d.dados) {
          setForm({
            cargo: d.dados.cargo || '',
            foto_url: d.dados.foto_url || '',
            telefone_comercial: d.dados.telefone_comercial || '',
            crea: d.dados.crea || '',
            crea_rnp: d.dados.crea_rnp || '',
            crea_titulo: d.dados.crea_titulo || '',
            linkedin: d.dados.linkedin || '',
          });
        }
      } catch (e: any) {
        // sem dados ainda — ok
      } finally { setCarregando(false); }
    })();
  }, [email]);

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_atualizar_dados_profissionais', email, ...form },
        { headers: { Authorization: token } });
      onSaved();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  const fld = (label: string, key: keyof typeof form, placeholder?: string) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>{label}</label>
      <input type="text" value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder} disabled={salvando || carregando}
        style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 5,
          border: '0.5px solid #ddd', boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !salvando && onClose()}>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8,
        width: 520, maxWidth: '92%', maxHeight: '88vh', overflowY: 'auto',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#020D7F' }}>
          Dados profissionais de {nome || email}
        </h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14, fontFamily: 'monospace' }}>
          {email}
        </div>

        {carregando ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando…</div>
        ) : (
          <>
            {fld('Cargo', 'cargo', 'ex: Engenheiro Hídrico')}
            {fld('Telefone comercial', 'telefone_comercial', '(51) 99999-9999')}
            {fld('Foto (URL)', 'foto_url', 'https://...')}
            {fld('CREA', 'crea', 'ex: RS-123456')}
            {fld('CREA RNP', 'crea_rnp', 'Registro Nacional de Profissional')}
            {fld('Título CREA', 'crea_titulo', 'ex: Engenheiro Civil')}
            {fld('LinkedIn', 'linkedin', 'https://linkedin.com/in/...')}
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: '#fff', color: '#555', border: '0.5px solid #ddd',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando || carregando}
            style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600,
              background: salvando ? '#888' : '#0930D0', color: '#fff', border: 'none',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: Criar perfil novo (E5) ────────────────────────────────────────
function ModalCriarPerfil({ onClose, onSaved }: {
  onClose: () => void; onSaved: () => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function criar() {
    if (!/^[a-z][a-z0-9_]*$/.test(codigo)) {
      alert('Código inválido. Use snake_case (letras minúsculas, números, underscore). Ex: gerente_projeto');
      return;
    }
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_criar_perfil',
          codigo: codigo.trim(), nome: nome.trim(),
          descricao: descricao.trim() || undefined },
        { headers: { Authorization: token } });
      alert(`Perfil "${codigo}" criado. Atribua permissões clicando nas células da matriz.`);
      onSaved();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !salvando && onClose()}>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8,
        width: 460, maxWidth: '92%', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#020D7F' }}>
          Novo perfil
        </h3>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Código *</label>
        <input type="text" value={codigo}
          onChange={e => setCodigo(e.target.value.toLowerCase().replace(/\s/g, '_'))}
          placeholder="ex: gerente_projeto" disabled={salvando}
          style={{ width: '100%', padding: '7px 10px', fontSize: 12, fontFamily: 'monospace',
            borderRadius: 5, border: '0.5px solid #ddd', marginBottom: 4, boxSizing: 'border-box' }} />
        <div style={{ fontSize: 10, color: '#888', marginBottom: 12 }}>
          snake_case · sem espaços · usado como chave interna (não muda depois)
        </div>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Nome *</label>
        <input type="text" value={nome} onChange={e => setNome(e.target.value)}
          placeholder="ex: Gerente de Projeto" disabled={salvando}
          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 5,
            border: '0.5px solid #ddd', marginBottom: 12, boxSizing: 'border-box' }} />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Descrição</label>
        <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
          rows={2} disabled={salvando}
          placeholder="Pra que serve esse perfil?"
          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 5,
            border: '0.5px solid #ddd', marginBottom: 18, boxSizing: 'border-box',
            fontFamily: 'inherit', resize: 'vertical' }} />

        <div style={{ fontSize: 10, color: '#888', marginBottom: 14, background: '#EEF1FF',
          padding: 8, borderRadius: 4, border: '0.5px solid #D6DDFF' }}>
          ℹ️ Perfil criado sem permissões. Depois clique nas células da matriz pra atribuir.
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: '#fff', color: '#555', border: '0.5px solid #ddd',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            Cancelar
          </button>
          <button onClick={criar} disabled={salvando || !codigo || !nome.trim()}
            style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600,
              background: salvando ? '#888' : '#0930D0', color: '#fff', border: 'none',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            {salvando ? 'Criando...' : 'Criar perfil'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: Editar/excluir perfil (E5) ────────────────────────────────────
function ModalEditarPerfil({ perfil, onClose, onChanged }: {
  perfil: Perfil; onClose: () => void; onChanged: () => void;
}) {
  const [nome, setNome] = useState(perfil.nome);
  const [descricao, setDescricao] = useState(perfil.descricao || '');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_atualizar_perfil',
          codigo: perfil.codigo, nome: nome.trim(),
          descricao: descricao.trim() },
        { headers: { Authorization: token } });
      onChanged();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir() {
    const conf = window.prompt(`Para confirmar a exclusão, digite o código: ${perfil.codigo}`);
    if (conf !== perfil.codigo) {
      if (conf !== null) alert('Código não confere — exclusão cancelada.');
      return;
    }
    if (!window.confirm(`Excluir o perfil "${perfil.codigo}"?\n\nIsso remove TAMBÉM:\n• Todas as permissões atribuídas ao perfil\n• Todos os vínculos com usuários (mesmo inativos)\n\nIrreversível.`)) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_excluir_perfil',
          codigo: perfil.codigo,
          confirmacao_codigo: perfil.codigo },
        { headers: { Authorization: token } });
      onChanged();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={() => !salvando && onClose()}>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8,
        width: 460, maxWidth: '92%', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#020D7F' }}>
          Editar perfil
        </h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14, fontFamily: 'monospace' }}>
          código: {perfil.codigo} · {perfil.qtd_permissoes ?? 0} permissões · {perfil.qtd_usuarios ?? 0} usuário(s)
        </div>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Nome</label>
        <input type="text" value={nome} onChange={e => setNome(e.target.value)}
          disabled={salvando}
          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 5,
            border: '0.5px solid #ddd', marginBottom: 12, boxSizing: 'border-box' }} />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>Descrição</label>
        <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
          rows={2} disabled={salvando}
          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 5,
            border: '0.5px solid #ddd', marginBottom: 18, boxSizing: 'border-box',
            fontFamily: 'inherit', resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
          <button onClick={excluir} disabled={salvando}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
              background: '#fff', color: '#B91626', border: '0.5px solid #FBD9D9',
              borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
            🗑️ Excluir
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} disabled={salvando}
              style={{ padding: '7px 14px', fontSize: 12, fontWeight: 500,
                background: '#fff', color: '#555', border: '0.5px solid #ddd',
                borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
              Cancelar
            </button>
            <button onClick={salvar} disabled={salvando || !nome.trim()}
              style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600,
                background: salvando ? '#888' : '#0930D0', color: '#fff', border: 'none',
                borderRadius: 4, cursor: salvando ? 'not-allowed' : 'pointer' }}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ABA 2: MATRIZ PERFIS × PERMISSOES (E2 read + E3 write + E5 CRUD) ─────
function AbaMatriz() {
  const [data, setData] = useState<Matriz | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());
  // E3: estado mutável das atribuições (toggle ao vivo)
  const [paresLive, setParesLive] = useState<Set<string>>(new Set());
  const [salvandoToggle, setSalvandoToggle] = useState<string | null>(null);
  // E5: CRUD perfis
  const [criandoPerfil, setCriandoPerfil] = useState(false);
  const [editandoPerfil, setEditandoPerfil] = useState<Perfil | null>(null);

  async function carregar() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_listar_matriz' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setData(d);
      setParesLive(new Set(d.atribuicoes.map((a: Atribuicao) => `${a.perfil_codigo}|${a.permissao_codigo}`)));
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function togglePerm(perfilCodigo: string, permCodigo: string) {
    const chave = `${perfilCodigo}|${permCodigo}`;
    if (salvandoToggle) return;
    setSalvandoToggle(chave);
    const ativarAgora = !paresLive.has(chave);
    // Optimistic update
    setParesLive(prev => {
      const n = new Set(prev);
      if (ativarAgora) n.add(chave); else n.delete(chave);
      return n;
    });
    try {
      const token = await getToken();
      const resp = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'admin_perms_toggle_perm_perfil',
          perfil_codigo: perfilCodigo,
          permissao_codigo: permCodigo,
          ativar: ativarAgora },
        { headers: { Authorization: token } });
      const retorno = resp.data.body ? JSON.parse(resp.data.body) : resp.data;
      if (ativarAgora && permCodigo.startsWith('nexo.') && retorno.vinculos_sem_identidade > 0) {
        alert(`Permissão NEXO ativada. ${retorno.vinculos_sem_identidade} vínculo(s) órfão(s) ficaram sem acesso até existir uma conta Cognito e a atribuição ser confirmada novamente.`);
      }
    } catch (e: any) {
      // Rollback no erro
      setParesLive(prev => {
        const n = new Set(prev);
        if (ativarAgora) n.delete(chave); else n.add(chave);
        return n;
      });
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvandoToggle(null); }
  }

  // Set lookup O(1)
  const pares = paresLive;
  void data;  // referência preservada

  function toggleColapso(modulo: string) {
    setColapsados(prev => {
      const n = new Set(prev);
      if (n.has(modulo)) n.delete(modulo); else n.add(modulo);
      return n;
    });
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando matriz…</div>;
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: '#bbb' }}>Sem dados.</div>;

  // Filtro: busca por código ou descrição
  const filtraPerm = (p: PermissaoCat) => {
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return p.codigo.toLowerCase().includes(q)
        || (p.descricao || '').toLowerCase().includes(q)
        || (p.pagina || '').toLowerCase().includes(q)
        || (p.sub_pagina || '').toLowerCase().includes(q);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="🔎 Buscar permissão (código, descrição, página)…"
          value={busca} onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, minWidth: 280, maxWidth: 480, padding: '7px 10px',
            fontSize: 12, borderRadius: 5, border: '0.5px solid #ddd' }} />
        <span style={{ fontSize: 11, color: '#888' }}>
          {data.total_perfis} perfis · {data.total_permissoes} permissões
        </span>
        <button onClick={() => setCriandoPerfil(true)}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
            background: '#0930D0', color: '#fff', border: 'none',
            borderRadius: 4, cursor: 'pointer' }}>
          + Novo perfil
        </button>
      </div>

      <div style={{ fontSize: 10, color: '#888', marginBottom: 10 }}>
        ✓ = perfil tem a permissão. <strong>Clique na célula pra ligar/desligar</strong>. Mudanças comuns refletem em ~5min (cache).
        Clique no <strong>código do perfil</strong> (cabeçalho) pra editar/excluir.
      </div>

      <div style={{ fontSize: 10, color: '#344054', marginBottom: 12, padding: 8,
        background: '#F4F7FF', border: '0.5px solid #D6DDFF', borderRadius: 4 }}>
        Ao ligar uma permissão <code>nexo.*</code>, o sistema vincula os membros atuais desse perfil
        às contas Cognito existentes. Vínculos antigos de outra conta não são transferidos pelo email.
      </div>

      {data.modulos.map(mod => {
        const permsFilt = mod.permissoes.filter(filtraPerm);
        if (busca && permsFilt.length === 0) return null;
        const colapsado = colapsados.has(mod.modulo);

        return (
          <div key={mod.modulo} style={{ marginBottom: 18 }}>
            <div onClick={() => toggleColapso(mod.modulo)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                background: '#F0F1F5', borderRadius: 5, cursor: 'pointer',
                marginBottom: colapsado ? 0 : 8 }}>
              <span style={{ fontSize: 12 }}>{colapsado ? '▶' : '▼'}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#020D7F' }}>{mod.modulo}</span>
              <span style={{ fontSize: 11, color: '#888' }}>
                ({permsFilt.length}{busca ? ` de ${mod.total}` : ''} permissões)
              </span>
            </div>

            {!colapsado && (
              <div style={{ overflowX: 'auto', border: '0.5px solid #e0e0e0', borderRadius: 5 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, background: '#fff' }}>
                  <thead>
                    <tr style={{ background: '#FAFBFF', borderBottom: '0.5px solid #e0e0e0' }}>
                      <th style={{ padding: 8, textAlign: 'left', minWidth: 260, position: 'sticky', left: 0,
                        background: '#FAFBFF', zIndex: 1 }}>Permissão</th>
                      {data.perfis.map(p => (
                        <th key={p.codigo}
                          onClick={() => setEditandoPerfil(p)}
                          title={`${p.nome}\n${p.descricao || ''}\n\n(Clique pra editar)`}
                          style={{ padding: 8, textAlign: 'center', minWidth: 70, fontSize: 10,
                            writingMode: 'horizontal-tb', color: '#020D7F',
                            cursor: 'pointer', userSelect: 'none' }}>
                          {p.codigo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permsFilt.map(perm => (
                      <tr key={perm.codigo} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                        <td style={{ padding: 8, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                          <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#020D7F' }}>
                            {perm.codigo}
                          </div>
                          <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>
                            {perm.descricao || perm.pagina}
                          </div>
                        </td>
                        {data.perfis.map(p => {
                          const chave = `${p.codigo}|${perm.codigo}`;
                          const tem = pares.has(chave);
                          const salvando = salvandoToggle === chave;
                          return (
                            <td key={p.codigo}
                              onClick={() => !salvando && togglePerm(p.codigo, perm.codigo)}
                              title={tem ? `Clique pra remover ${p.codigo} → ${perm.codigo}` : `Clique pra atribuir ${p.codigo} → ${perm.codigo}`}
                              style={{ padding: 8, textAlign: 'center',
                                background: salvando ? '#FFF8E8' : (tem ? '#DCFCE7' : 'transparent'),
                                color: tem ? '#166534' : '#ddd', fontWeight: 600,
                                cursor: salvando ? 'wait' : 'pointer',
                                userSelect: 'none' }}>
                              {salvando ? '…' : (tem ? '✓' : '·')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {criandoPerfil && (
        <ModalCriarPerfil
          onClose={() => setCriandoPerfil(false)}
          onSaved={() => { setCriandoPerfil(false); carregar(); }} />
      )}
      {editandoPerfil && (
        <ModalEditarPerfil perfil={editandoPerfil}
          onClose={() => setEditandoPerfil(null)}
          onChanged={() => { setEditandoPerfil(null); carregar(); }} />
      )}
    </div>
  );
}
